/**
 * Mutations do CHAT: criação manual, mensagens (com comentário interno),
 * leitura, assumir/transferir/sair/encerrar, vínculos (PUT) e as ações do
 * widget público (start/message/rate — sem auth).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiDownload, apiUrl, authHeaders } from '@/lib/api-client';
import { PRESENCE_KEY } from '@/features/presence/presence-queries';
import type {
  ChatArchiveResponse,
  ChatCloseReason,
  ChatCloseReasonCreateBody,
  ChatCloseReasonUpdateBody,
  ChatCreateBody,
  ChatDetail,
  ChatMessage,
  ChatMessageBody,
  ChatTranscriptFormat,
  ChatContactBody,
  ChatUpdateBody,
  PublicChatState,
} from '@erp/shared';
import {
  CHATS_KEY,
  chatMessagesKey,
  closeReasonsKey,
  publicChatKey,
  resyncChatMessages,
  whatsappWindowKey,
} from './chats-queries';

export function useCreateChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ChatCreateBody) => {
      const res = await api<ChatDetail>('/chats', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHATS_KEY }),
  });
}

export function useSendChatMessage(chatId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ChatMessageBody) => {
      const res = await api<ChatMessage>(`/chats/${chatId}/messages`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      // O refetch das mensagens é incremental (after_id) — só puxa a nova.
      void qc.invalidateQueries({ queryKey: chatMessagesKey(chatId) });
      void qc.invalidateQueries({ queryKey: [...CHATS_KEY, 'list'] });
    },
  });
}

/**
 * "Tentar de novo" (spec 028, FR-006): POST /chats/:id/messages/:mid/resend — a
 * resposta que falhou volta como mensagem NOVA. A falhada também muda (fica
 * "reenviada"), então o refetch das mensagens é COMPLETO, e não só o append.
 */
export function useResendChatMessage(chatId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: number) => {
      const res = await api<ChatMessage>(`/chats/${chatId}/messages/${messageId}/resend`, {
        method: 'POST',
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void resyncChatMessages(qc, chatId);
      void qc.invalidateQueries({ queryKey: [...CHATS_KEY, 'list'] });
    },
  });
}

/**
 * POST /chats/:id/media — multipart {file, caption?} (v8 Etapa 3b). api()
 * serializa body como JSON, então aqui é fetch cru com FormData + bearer,
 * lançando um ApiError-like em falha (mesmo shape que errorMessage espera).
 */
export function useSendChatMedia(chatId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, caption }: { file: File; caption?: string }) => {
      const form = new FormData();
      form.append('file', file);
      if (caption) form.append('caption', caption);
      const headers: Record<string, string> = authHeaders();
      const res = await fetch(apiUrl(`/chats/${chatId}/media`), {
        method: 'POST',
        headers,
        body: form,
      });
      if (!res.ok) {
        let body: { error?: string } = {};
        try {
          body = (await res.json()) as { error?: string };
        } catch {
          /* corpo de erro não-JSON (413 do proxy, por exemplo) */
        }
        throw {
          status: res.status,
          error: body.error ?? (res.status === 413 ? 'file_too_large' : `http_${res.status}`),
        };
      }
      return (await res.json()) as ChatMessage;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chatMessagesKey(chatId) });
      void qc.invalidateQueries({ queryKey: [...CHATS_KEY, 'list'] });
    },
  });
}

/** Corpo do envio de modelo HSM (POST /chats/:id/template e /chats/whatsapp). */
export interface ChatTemplateBody {
  name: string;
  language: string;
  params: string[];
}

/** POST /chats/:id/template — reabre a janela de 24h com um modelo HSM. */
export function useSendChatTemplate(chatId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ChatTemplateBody) => {
      const res = await api<ChatMessage>(`/chats/${chatId}/template`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chatMessagesKey(chatId) });
      void qc.invalidateQueries({ queryKey: [...CHATS_KEY, 'list'] });
      // O envio do modelo reabre a janela — refetch imediato do estado.
      void qc.invalidateQueries({ queryKey: whatsappWindowKey(chatId) });
    },
  });
}

/**
 * POST /chats/whatsapp — conversa ATIVA: abre chat outbound num canal
 * conectado enviando um modelo HSM. 409 chat_already_open quando o número já
 * tem conversa aberta (details pode carregar o id).
 */
export function useStartWhatsappChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      channelId: number;
      to: string;
      template: ChatTemplateBody;
      /** spec 057: aberta de dentro do ticket */
      ticketNumber?: number;
      clientId?: number;
    }) => {
      const res = await api<{ id: number }>('/chats/whatsapp', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHATS_KEY }),
  });
}

/** POST /chats/:id/read — zera o unreadCount do atendente na lista. */
export function useMarkChatRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (chatId: number) => {
      const res = await api<void>(`/chats/${chatId}/read`, { method: 'POST' });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...CHATS_KEY, 'list'] });
      void qc.invalidateQueries({ queryKey: [...CHATS_KEY, 'detail'] });
    },
  });
}

function chatAction(path: 'leave' | 'resume') {
  return async (chatId: number) => {
    const res = await api<ChatDetail>(`/chats/${chatId}/${path}`, { method: 'POST' });
    if (res.error) throw res.error;
    return res.data;
  };
}

/**
 * Assumir. Número = o assumir normal da fila; `{chatId, takeover: true}` =
 * "Assumir desta pessoa" (conversa com OUTRO atendente — a API exige
 * chat.manage_others e responde 403 sem ela, spec 013). `endPause: true`
 * (spec 015) é a resposta ao 409 attendant_paused: encerra a pausa e assume
 * na mesma transação — por isso o status (PRESENCE_KEY) também é refeito.
 */
export type AssumeChatInput = number | { chatId: number; takeover?: boolean; endPause?: boolean };

export function useAssumeChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssumeChatInput) => {
      const { chatId, takeover = false, endPause = false } =
        typeof input === 'number' ? { chatId: input } : input;
      const body = { ...(takeover ? { takeover: true } : {}), ...(endPause ? { endPause: true } : {}) };
      const res = await api<ChatDetail>(`/chats/${chatId}/assume`, {
        method: 'POST',
        ...(Object.keys(body).length > 0 ? { body } : {}),
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: CHATS_KEY });
      if (typeof input !== 'number' && input.endPause) void qc.invalidateQueries({ queryKey: PRESENCE_KEY });
    },
  });
}

export function useLeaveChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: chatAction('leave'),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHATS_KEY }),
  });
}

/**
 * Retomar conversa ENCERRADA (spec 013). 409 chat_already_open traz em
 * details.id a conversa aberta do contato — quem chama abre a aberta.
 */
export function useResumeChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: chatAction('resume'),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHATS_KEY }),
  });
}

/**
 * Baixar a conversa inteira — o PDF (o documento, padrão) ou o texto puro —
 * com o nome igual ao do Content-Disposition.
 */
export function downloadChatTranscript(
  chatId: number,
  format: ChatTranscriptFormat = 'pdf'
): Promise<void> {
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
  return apiDownload(
    `/chats/${chatId}/transcript?format=${format}`,
    `conversa-${chatId}-${day}.${format}`
  );
}

/** Destino da transferência (v8 Etapa 4): EXATAMENTE um — atendente OU mesa
 *  (a API responde 422 invalid_transfer fora disso). Transferir para mesa
 *  devolve o chat à Entrada do departamento (limpa o responsável). */
export type ChatTransferTarget = { userId: number } | { deskId: number };

export function useTransferChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ chatId, ...body }: { chatId: number } & ChatTransferTarget) => {
      const res = await api<ChatDetail>(`/chats/${chatId}/transfer`, {
        method: 'POST',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHATS_KEY }),
  });
}

/**
 * Finalizar (canceled=false) ou Cancelar (canceled=true) — sempre com motivo
 * (spec 013). `appointment` pede o apontamento com a transcrição no ticket; o
 * desfecho volta em appointmentId/appointmentError (falha não impede o
 * encerramento — a tela avisa).
 */
export function useArchiveChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      chatId,
      ...body
    }: {
      chatId: number;
      canceled: boolean;
      reasonId: number;
      note: string | null;
      appointment: boolean;
    }) => {
      const res = await api<ChatArchiveResponse>(`/chats/${chatId}/archive`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHATS_KEY }),
  });
}

// ── Motivos de encerramento (configuração, spec 013) ────────────────────────

export function useCreateCloseReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ChatCloseReasonCreateBody) => {
      const res = await api<ChatCloseReason>('/chat/close-reasons', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: closeReasonsKey }),
  });
}

export function useUpdateCloseReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number } & ChatCloseReasonUpdateBody) => {
      const res = await api<ChatCloseReason>(`/chat/close-reasons/${id}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: closeReasonsKey }),
  });
}

export function useDeleteCloseReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/chat/close-reasons/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: closeReasonsKey }),
  });
}

export function useReorderCloseReasons() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const res = await api<ChatCloseReason[]>('/chat/close-reasons/order', {
        method: 'PUT',
        body: { ids },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: closeReasonsKey }),
  });
}

async function putChat(chatId: number, body: ChatUpdateBody): Promise<ChatDetail> {
  const res = await api<ChatDetail>(`/chats/${chatId}`, { method: 'PUT', body });
  if (res.error) throw res.error;
  return res.data;
}

/** PUT /chats/:id/contact (spec 056) — o nome e o e-mail da pessoa da conversa. */
export function useUpdateChatContact(chatId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ChatContactBody): Promise<ChatDetail> => {
      const res = await api<ChatDetail>(`/chats/${chatId}/contact`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHATS_KEY }),
  });
}

/** PUT /chats/:id — vincula cliente, departamento (mesa) ou ticket. */
export function useUpdateChat(chatId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ChatUpdateBody) => putChat(chatId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHATS_KEY }),
  });
}

/**
 * Mesma rota, com o id na VARIÁVEL — para quem só conhece o chat depois de
 * agir (v12: a conversa ativa aberta de dentro do ticket, que nasce já
 * vinculada ao ticket e ao cliente).
 */
export function useUpdateAnyChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, ...body }: { chatId: number } & ChatUpdateBody) =>
      putChat(chatId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHATS_KEY }),
  });
}

// ── Widget público (sem auth) ───────────────────────────────────────────────

/** `tenant` é o seletor `?tenant=<slug>` exigido pelo publicTenantMiddleware
 *  nas rotas públicas sem recurso na URL (o widget repassa o da própria página). */
export function usePublicStartChat(tenant?: string) {
  return useMutation({
    mutationFn: async (body: { name: string; phone?: string }) => {
      const res = await api<{ token: string }>('/public/chat', {
        method: 'POST',
        query: { tenant },
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function usePublicSendMessage(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (text: string) => {
      const res = await api<ChatMessage>(`/public/chat/${token}/messages`, {
        method: 'POST',
        body: { text },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: publicChatKey(token) }),
  });
}

export function usePublicRateChat(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assessment: number) => {
      const res = await api<PublicChatState>(`/public/chat/${token}/rate`, {
        method: 'POST',
        body: { assessment },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: publicChatKey(token) }),
  });
}
